import { createApp } from '@backstage/frontend-defaults';
import {
  ApiBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  AppRootWrapperBlueprint,
  ThemeBlueprint,
} from '@backstage/plugin-app-react';
import {
  convertLegacyPageExtension,
  convertLegacyPlugin,
} from '@backstage/core-compat-api';
import { convertLegacyEntityCardExtension } from '@backstage/plugin-catalog-react/alpha';
import devcontainersCatalogModule from '@coder/backstage-plugin-devcontainers-react';
import { ScmAuth } from '@backstage/integration-react';
import rdapGithubPlugin from '@internal/backstage-plugin-rdap-github';
import knowledgeScaffolderModule, {
  knowledgeGlobalAssistantModule,
} from '@internal/backstage-plugin-knowledge';
import codeBuilderScaffolderModule from '@internal/backstage-plugin-code-builder';
import {
  rdapScaffolderFieldsModule,
  UnsavedWizardGuard,
} from '@internal/backstage-plugin-rdap-scaffolder-fields';
import { catalogRdapFrontendModule } from '@internal/backstage-plugin-catalog-rdap-frontend';
import { RdapCatalogGraphPage } from '@internal/rdap-knowledge-graph';
import {
  catalogGraphPlugin,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import { UnifiedThemeProvider } from '@backstage/theme';
import { customCatalogPlugin } from './modules/catalog/customCatalogPlugin';
import DarkIcon from '@material-ui/icons/Brightness2';
import LightIcon from '@material-ui/icons/WbSunny';
import { appModuleOidc } from './modules/auth/appModuleOidc';
import { navModule } from './modules/nav';
import { CustomHomePageLayout } from './modules/home/HomePage';
import './styles.css';
import { darkTheme, lightTheme } from './themes';
import { RouteTitle } from './modules/title/RouteTitle';

import { HomePageLayoutBlueprint } from '@backstage/plugin-home-react/alpha';
import awsCodepipelinePlugin from '@internal/backstage-plugin-aws-codepipeline';

import techDocsPlugin from '@backstage/plugin-techdocs/alpha';
import notificationsPlugin from '@backstage/plugin-notifications/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import searchPlugin from '@backstage/plugin-search/alpha';
import homePlugin from '@backstage/plugin-home/alpha';
import announcementsPlugin from '@backstage-community/plugin-announcements/alpha';
import { techDocsMermaidAddonModule } from 'backstage-plugin-techdocs-addon-mermaid/alpha';
// The Catalog Graph alpha page only accepts statically configured roots. The
// R-DAP graph must discover the catalog's Tenant roots at runtime, otherwise
// opening /catalog-graph from the sidebar produces an empty canvas.
const convertedCatalogGraphPlugin = convertLegacyPlugin(catalogGraphPlugin, {
  extensions: [
    convertLegacyEntityCardExtension(EntityCatalogGraphCard, {
      name: 'relations',
    }),
    convertLegacyPageExtension(RdapCatalogGraphPage, {
      name: 'knowledge-graph',
      path: '/catalog-graph',
    }),
  ],
});

const scmAuthModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    ApiBlueprint.make({
      name: 'scm-auth',
      params: defineParams => defineParams(ScmAuth.createDefaultApiFactory()),
    }),
  ],
});

const customHomePageModule = createFrontendModule({
  pluginId: 'home',
  extensions: [
    HomePageLayoutBlueprint.make({
      params: {
        loader: async () => CustomHomePageLayout,
      },
    }),
  ],
});

const CreatePageHeader = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const isCreatePage =
    location.pathname === '/create' || location.pathname.startsWith('/create/');
  const [sidebarPage, setSidebarPage] = useState<HTMLElement>();

  useEffect(() => {
    if (!isCreatePage) {
      setSidebarPage(undefined);
      return undefined;
    }

    const page = document.querySelector<HTMLElement>(
      '[class*="BackstageSidebarPage-root"]',
    );

    if (!page) {
      return undefined;
    }

    page.classList.add('rdap-create-page');
    setSidebarPage(page);

    return () => {
      page.classList.remove('rdap-create-page');
    };
  }, [isCreatePage]);

  return (
    <>
      {isCreatePage &&
        sidebarPage &&
        createPortal(
          <div className="rdap-create-page-header" role="banner">
            <h1>R-DAP Create</h1>
          </div>,
          sidebarPage,
        )}
      {children}
    </>
  );
};

const createPageHeaderModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootWrapperBlueprint.make({
      name: 'rdap-create-page-header',
      params: {
        component: CreatePageHeader,
      },
    }),
  ],
});

const routeTitleModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootWrapperBlueprint.make({
      name: 'rdap-route-title',
      params: {
        component: RouteTitle,
      },
    }),
  ],
});

const unsavedWizardGuardModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootWrapperBlueprint.make({
      name: 'rdap-unsaved-wizard-guard',
      params: {
        component: UnsavedWizardGuard,
      },
    }),
  ],
});

const appThemeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    ThemeBlueprint.make({
      name: 'light',
      params: {
        theme: {
          id: 'rdap-light',
          title: 'RDAP Light',
          variant: 'light',
          icon: <LightIcon />,
          Provider: ({ children }: { children: ReactNode }) => (
            <UnifiedThemeProvider theme={lightTheme} children={children} />
          ),
        },
      },
    }),
    ThemeBlueprint.make({
      name: 'dark',
      params: {
        theme: {
          id: 'rdap-dark',
          title: 'RDAP Dark',
          variant: 'dark',
          icon: <DarkIcon />,
          Provider: ({ children }: { children: ReactNode }) => (
            <UnifiedThemeProvider theme={darkTheme} children={children} />
          ),
        },
      },
    }),
  ],
});

const app = createApp({
  features: [
    customCatalogPlugin,
    catalogRdapFrontendModule,
    convertedCatalogGraphPlugin,
    awsCodepipelinePlugin,
    notificationsPlugin,
    scaffolderPlugin,
    searchPlugin,
    homePlugin,
    announcementsPlugin,
    rdapGithubPlugin,
    scmAuthModule,
    navModule,
    customHomePageModule,
    createPageHeaderModule,
    routeTitleModule,
    unsavedWizardGuardModule,
    appThemeModule,
    appModuleOidc,
    knowledgeScaffolderModule,
    knowledgeGlobalAssistantModule,
    codeBuilderScaffolderModule,
    rdapScaffolderFieldsModule,
    devcontainersCatalogModule,
    techDocsPlugin,
    techDocsMermaidAddonModule,
  ],
});

export default app.createRoot();
